/**
 * useWishlist, Sprint 2.2c.
 *
 * Customer-side wishlist for SKUs surfaced in /chat and /browse. Backed
 * by the `wishlists` table (migration 20260514010000_wishlists.sql).
 *
 * Three exports:
 *   - useWishlist(userId)    , read all saved SKUs (joined with
 *                                vendor_products for card rendering)
 *   - useWishlistSkuIds(userId), lightweight Set<string> of saved
 *                                sku_ids for the heart-toggle button
 *                                (avoids re-fetching the joined rows
 *                                when all the card needs is "is this
 *                                one saved?")
 *   - useWishlistMutations() , add / remove mutations with optimistic
 *                                cache updates so the heart toggles
 *                                instantly without waiting for the
 *                                round-trip
 *
 * Anon visitors: hooks no-op when userId is null/undefined (enabled
 * gating on the queries; the mutations bail early). Anon-side wishlist
 * is out of scope for v1, the magic-link signup CTA on /order/success
 * is the pathway from anon → authed.
 *
 * Cache key strategy: ["wishlist", userId, "rows"] for the joined view,
 * ["wishlist", userId, "ids"] for the lightweight set. Both invalidated
 * by the mutations.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface WishlistRow {
  id: string;                  // wishlists.id (the join row)
  sku_id: string;
  created_at: string;
  // Joined vendor_products fields needed to render a SkuCard.
  sku: {
    id: string;
    name: string;
    slug: string | null;
    description: string | null;
    price: number;
    image_url: string | null;
    is_voucher: boolean | null;
    vendor_category: string;
    vendor_name: string;
    // MS327: typed for SkuCardData parity (MS312 lead-time gate +
    // MS318 recipient-conflict warning carry through wishlist →
    // ProductDetailModal → OrderConfirmSheet without losing data).
    lead_time_days: number | null;
    dietary_tags: string[] | null;
  } | null;
}

/**
 * Full wishlist with vendor_products joined in. Used by /wishlist to
 * render the saved-items grid.
 */
export function useWishlist(userId: string | null | undefined) {
  return useQuery({
    queryKey: ["wishlist", userId, "rows"],
    enabled: Boolean(userId),
    staleTime: 30_000,
    queryFn: async (): Promise<WishlistRow[]> => {
      if (!userId) return [];
      // Two queries (rather than a join via supabase resource embedding)
      // because vendor_products RLS may differ from wishlists RLS in a
      // future tighten, keeping them separate keeps the failure modes
      // independent. Volume is tiny (one user's saves) so the extra
      // round-trip doesn't matter.
      const { data: rows, error } = await supabase
        .from("wishlists")
        .select("id, sku_id, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      const wishlistRows = (rows as unknown as Array<{
        id: string;
        sku_id: string;
        created_at: string;
      }> | null) ?? [];
      if (wishlistRows.length === 0) return [];

      const skuIds = wishlistRows.map((r) => r.sku_id);
      const { data: skus, error: skusErr } = await supabase
        .from("vendor_products")
        .select(
          "id, name, slug, description, price, image_url, is_voucher, vendor_category, lead_time_days, dietary_tags, vendor_partners(vendor_name)",
        )
        .in("id", skuIds)
        .eq("is_available", true)
        .is("deleted_at", null);
      if (skusErr) throw skusErr;
      const skuMap = new Map<string, WishlistRow["sku"]>();
      for (const s of (skus as unknown as Array<{
        id: string;
        name: string;
        slug: string | null;
        description: string | null;
        price: number;
        image_url: string | null;
        is_voucher: boolean | null;
        vendor_category: string | null;
        lead_time_days: number | null;
        dietary_tags: string[] | null;
        vendor_partners: { vendor_name?: string } | { vendor_name?: string }[] | null;
      }> | null) ?? []) {
        const vp = s.vendor_partners;
        const vendorName = Array.isArray(vp) ? vp[0]?.vendor_name : vp?.vendor_name;
        skuMap.set(s.id, {
          id: s.id,
          name: s.name,
          slug: s.slug,
          description: s.description,
          price: s.price,
          image_url: s.image_url,
          is_voucher: s.is_voucher,
          vendor_category: s.vendor_category ?? "gift",
          vendor_name: vendorName ?? "Lembra",
          lead_time_days: s.lead_time_days,
          dietary_tags: s.dietary_tags,
        });
      }
      // Preserve the wishlist row order (newest first); attach sku or
      // null if the underlying vendor_product was deleted between save
      // and read (rare but real, handle gracefully on the page). MS327
      // adds vendor-paused (is_available=false) to the same "missing
      // sku" branch — Wishlist already renders the missing-sku
      // fallback per existing skuMap.get() ?? null pattern.
      return wishlistRows.map((w) => ({
        id: w.id,
        sku_id: w.sku_id,
        created_at: w.created_at,
        sku: skuMap.get(w.sku_id) ?? null,
      }));
    },
  });
}

/**
 * Set of saved sku_ids only. Used by SkuCard's heart-toggle button to
 * decide whether to render the "saved" or "save" state without
 * re-fetching the joined wishlist rows.
 *
 * Returned as a Set for O(1) membership checks. The query function
 * builds the Set from a fresh fetch each time the cache invalidates.
 */
export function useWishlistSkuIds(userId: string | null | undefined) {
  return useQuery({
    queryKey: ["wishlist", userId, "ids"],
    enabled: Boolean(userId),
    staleTime: 30_000,
    queryFn: async (): Promise<Set<string>> => {
      if (!userId) return new Set();
      const { data, error } = await supabase
        .from("wishlists")
        .select("sku_id")
        .eq("user_id", userId);
      if (error) throw error;
      const rows = (data as unknown as Array<{ sku_id: string }> | null) ?? [];
      return new Set(rows.map((r) => r.sku_id));
    },
  });
}

/**
 * Add / remove mutations with optimistic cache updates so the heart
 * toggle on SkuCard feels instantaneous. On error, the cache rolls
 * back and the toast surfaces the failure.
 */
export function useWishlistMutations(userId: string | null | undefined) {
  const queryClient = useQueryClient();
  const idsKey  = ["wishlist", userId, "ids"] as const;
  const rowsKey = ["wishlist", userId, "rows"] as const;

  const add = useMutation({
    mutationFn: async (skuId: string) => {
      if (!userId) throw new Error("Sign in to save gifts.");
      const { error } = await supabase
        .from("wishlists")
        .insert({ user_id: userId, sku_id: skuId });
      // Treat unique-constraint violation (already saved) as success —
      // the user might have double-tapped or two tabs are open. The
      // cache reflects the saved state either way.
      if (error && error.code !== "23505") throw error;
    },
    onMutate: async (skuId: string) => {
      await queryClient.cancelQueries({ queryKey: idsKey });
      const prev = queryClient.getQueryData<Set<string>>(idsKey);
      if (prev) {
        const next = new Set(prev);
        next.add(skuId);
        queryClient.setQueryData(idsKey, next);
      }
      return { prev };
    },
    onError: (_err, _skuId, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(idsKey, ctx.prev);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: idsKey });
      void queryClient.invalidateQueries({ queryKey: rowsKey });
    },
  });

  const remove = useMutation({
    mutationFn: async (skuId: string) => {
      if (!userId) throw new Error("Sign in to manage saved gifts.");
      const { error } = await supabase
        .from("wishlists")
        .delete()
        .eq("user_id", userId)
        .eq("sku_id", skuId);
      if (error) throw error;
    },
    onMutate: async (skuId: string) => {
      await queryClient.cancelQueries({ queryKey: idsKey });
      const prev = queryClient.getQueryData<Set<string>>(idsKey);
      if (prev) {
        const next = new Set(prev);
        next.delete(skuId);
        queryClient.setQueryData(idsKey, next);
      }
      return { prev };
    },
    onError: (_err, _skuId, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(idsKey, ctx.prev);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: idsKey });
      void queryClient.invalidateQueries({ queryKey: rowsKey });
    },
  });

  return { add, remove };
}
